import React, { useEffect } from 'react';

import ls, { clear } from 'local-storage';
import { Cookies } from 'react-cookie';
import { useLocation, useSearchParams } from 'react-router-dom';

import { InfoOutlined } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Grid,
  ListItemText,
  Snackbar,
  Typography,
} from '@mui/material';

import sxaLoginImg from '../../assets/sxa-login-img.jpg';
import Alert from '../../components/Alert';
import KBAForm from '../../components/KBAForm';
import MFARequestForm from '../../components/MFARequestForm';
import MFAVerifyForm from '../../components/MFAVerifyForm';
import { Logo } from '../../components/fa-logo';
import Loading from '../../components/loading';
import { primaryBlue } from '../../constants/colors';
import {
  beginAuthAsync,
  clearAuthMessage,
  verifyAuthAsync,
} from '../../redux-store/auth/authSlice';
import {
  IBeginAuthRequest,
  IVerifyAuthRequest,
} from '../../redux-store/auth/authTypes';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { getUserLoginDetailsAsync } from '../../redux-store/ipenNotary/ipenNotarySlice';
import { RootState } from '../../redux-store/store';
import { getCodeVerifier, pkceChallengeFromVerifier } from '../../utils/pkce';
import { AuthChannel } from './AuthChannel';
import './_base.scss';

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const location: any = useLocation();
  const cookie = new Cookies();
  const config: any = useAppSelector((state: RootState) => state.global.config);
  const authLoading: boolean = useAppSelector(
    (state: RootState) => state.auth.loading
  );
  const authChallenge: any = useAppSelector(
    (state: RootState) => state.auth.challenge
  );
  const authSession: any = useAppSelector(
    (state: RootState) => state.auth.session
  );
  const message: any = useAppSelector((state: RootState) => state.auth.message);
  const hasLoggedIn: any = useAppSelector(
    (state: RootState) => state.ipenNotary.hasLoggedIn
  );
  const [searchParams] = useSearchParams();
  const [loginType, setLoginType] = React.useState('');
  const [redirectUrl, setRedirectUrl] = React.useState<null | string>(null);
  const [guestLoadingAction, setGuestLoadingAction] =
    React.useState<boolean>(false);
  const [guestConfirmationCode, setGuestConfirmationCode] =
    React.useState<string>('');
  const [guestLoginError, setGuestLoginError] = React.useState<null | string>(
    null
  );
  const [channel, setChannel] = React.useState<number>(0);
  const [isCreateAccount, setIsCreateAccount] = React.useState<boolean>(false);
  const [differenceIsOpen, setDifferenceIsOpen] =
    React.useState<boolean>(false);

  const orderId = searchParams.get('OrderId');
  const fullName = searchParams.get('FullName');
  const email = searchParams.get('Email');
  const userId = searchParams.get('UserId');

  const from = location.state?.from;

  const goStartLogin = (type: string) => {
    const registrationId: any = ls('registrationId');
    const phoneNumber: any = ls('phoneNumber');

    clear();
    if (registrationId) {
      ls('registrationId', registrationId);
    }
    if (phoneNumber) {
      ls('phoneNumber', phoneNumber);
    }

    const state = getCodeVerifier();
    ls('pkce_state', state);
    const codeVerifier = getCodeVerifier();
    ls('pkce_code_verifier', codeVerifier);
    pkceChallengeFromVerifier(codeVerifier)
      .then((codeChallenge) => {
        const params: any = {
          identity_provider: type,
          code_challenge: codeChallenge,
          state: state,
          code_challenge_method: 'S256',
          prompt: 'select_account',
        };
        const paramString = Object.keys(params)
          .map((key) => key + '=' + params[key])
          .join('&');
        const loginUrl = `${config.loginURL.base}${paramString}&${config.loginURL.params}`;
        if (redirectUrl) {
          cookie.set('postlogin_redirect', redirectUrl, { path: '/' });
        } else {
          cookie.remove('postlogin_redirect');
        }
        window.location.href = encodeURI(loginUrl);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (authChallenge) {
      if ('KBA_0' in authChallenge) {
        setLoginType('KBA');
      } else if ('CHANNEL_0' in authChallenge) {
        setLoginType('guest');
      }
    }
  }, [authChallenge]);

  const beginGuestAuth = (isCreateAccount: boolean) => {
    if (userId) {
      setGuestLoadingAction(true);
      const requestValues: IBeginAuthRequest = {
        userId: userId,
        ...(orderId && { orderId: orderId }),
        isCreateAccount: isCreateAccount,
      };
      dispatch(beginAuthAsync(requestValues)).then((res: any) => {
        if (res?.type === 'error') {
          setGuestLoginError(res.payload);
        } else {
          setGuestLoginError(null);
        }
        setGuestLoadingAction(false);
      });
    }
  };

  const goStartCreateOktaAccount = () => {
    setIsCreateAccount(true);
    beginGuestAuth(true);
  };

  const goVerifyGuestLoginCode = (
    answer: Record<string, string>,
    resendMfaCode: boolean
  ) => {
    if (userId) {
      setGuestLoadingAction(true);
      const requestBody: IVerifyAuthRequest = {
        ...(orderId && { orderId: orderId }),
        session: authSession,
        userId: userId,
        answers: answer,
        isCreateAccount: isCreateAccount,
        resendMfaCode: resendMfaCode,
        channel: channel,
      };
      dispatch(verifyAuthAsync(requestBody)).then((res: any) => {
        if (res.type === 'error') {
          setGuestLoginError(res?.text);
        } else {
          setGuestLoginError(null);
        }
        if (res.payload.Url) {
          window.location.href = encodeURI(res.payload.Url);
        } else {
          setGuestLoadingAction(false);
          setGuestConfirmationCode('');
        }
      });
    }
  };

  const goBackToMFAOptions = (
    answer: Record<string, string>,
    reselectMfaChannel: boolean
  ) => {
    if (userId) {
      setGuestLoadingAction(true);
      const requestBody: IVerifyAuthRequest = {
        ...(orderId && { orderId: orderId }),
        session: authSession,
        userId: userId,
        answers: answer,
        isCreateAccount: isCreateAccount,
        reselectMfaChannel: reselectMfaChannel,
      };
      dispatch(verifyAuthAsync(requestBody)).then((res: any) => {
        if (res.type === 'error') {
          setGuestLoginError(res?.text);
        } else {
          setGuestLoginError(null);
        }
        if (res.payload.Url) {
          window.location.href = encodeURI(res.payload.Url);
        } else {
          setGuestLoadingAction(false);
          setGuestConfirmationCode('');
        }
      });
    }
  };

  const goContinueGuestAuth = (channel: AuthChannel) => {
    if (userId) {
      setGuestLoadingAction(true);
      const requestBody: IVerifyAuthRequest = {
        ...(orderId && { orderId: orderId }),
        session: authSession,
        userId: userId,
        channel: channel,
        isCreateAccount: isCreateAccount,
      };
      dispatch(verifyAuthAsync(requestBody)).then((res: any) => {
        if (res.type === 'error') {
          setGuestLoginError(res?.text);
        } else {
          setGuestLoginError(null);
        }
        if (res.payload.Url) {
          window.location.href = encodeURI(res.payload.Url);
        } else {
          setGuestLoadingAction(false);
          setLoginType('guest-confirm');
        }
      });
    }
  };

  const goVerifyKBAAnswers = (answer: Record<string, string>) => {
    if (userId) {
      setGuestLoadingAction(true);
      const requestBody: IVerifyAuthRequest = {
        ...(orderId && { orderId: orderId }),
        session: authSession,
        userId: userId,
        answers: answer,
        isCreateAccount: isCreateAccount,
      };
      dispatch(verifyAuthAsync(requestBody)).then((res: any) => {
        if (res.type === 'error') {
          setGuestLoginError(res?.text);
        } else {
          setGuestLoginError(null);
        }
        if (res.payload.Url) {
          window.location.href = encodeURI(res.payload.Url);
        } else {
          setGuestLoadingAction(false);
        }
      });
    }
  };

  const goCancelFlow = () => {
    setLoginType('thirdPartyNew');
    setIsCreateAccount(false);
    clear();
  };

  useEffect(() => {
    if (userId && email) {
      dispatch(getUserLoginDetailsAsync({ email }));
      setLoginType('thirdPartyNew');
    } else {
      setLoginType('standard');
      if (from) {
        setRedirectUrl(
          `https://${window.location.host}${from.pathname}/${from.search}`
        );
      }
    }
  }, [email, searchParams]);

  useEffect(() => {
    if (hasLoggedIn === null) {
      return;
    }
    if (userId) {
      if (email) {
        if (hasLoggedIn) {
          setLoginType('thirdParty');
        } else {
          setLoginType('thirdPartyNew');
        }
      } else {
        setLoginType('thirdPartyNew');
      }
    }
  }, [hasLoggedIn]);

  if (authLoading) {
    return (
      <div className='full-page-loader'>
        <Loading showLogo text='Loading...' />
      </div>
    );
  } else {
    return (
      <div className='login-page'>
        <Box
          width='90%'
          minHeight='430px'
          height='calc(100vh - 45px)'
          display='flex'
          marginLeft='auto'
          marginRight='auto'
          justifyContent='center'
          alignItems='center'
          flexDirection='column'
          sx={{
            zIndex: 100,
          }}>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={message != null || guestLoginError != null}>
            <div>
              <Alert
                text={message?.message || guestLoginError}
                type={message?.type || 'error'}
                icon={<InfoOutlined />}
                elevated
                bold
                onClose={() => dispatch(clearAuthMessage())}
              />
            </div>
          </Snackbar>
          <Grid
            container
            alignItems='center'
            sx={{
              zIndex: 100,
              borderRadius: '4px',
              backgroundColor: 'white',
              boxShadow: '0px 3px 4px #00000024',
              justifyContent: 'center',
            }}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={8}
              xl={8}
              className='login-page__image'
              sx={{ borderRadius: '4px 0 0 4px' }}>
              <img
                src={sxaLoginImg}
                alt='man showing couple laptop screen'
                width='100%'
                style={{
                  display: 'block',
                  borderRadius: '4px 0 0 4px',
                }}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={9}
              md={9}
              lg={4}
              xl={4}
              px={3}
              pt={2}
              sx={{ flexGrow: 1 }}>
              <Box textAlign={'center'}>
                <Logo />
              </Box>
              <Box>
                <Box
                  display='flex'
                  flexDirection='column'
                  alignItems='center'
                  paddingLeft={4}
                  paddingRight={4}>
                  {(loginType === 'standard' || loginType === 'thirdParty') && (
                    <>
                      {/* TODO: uncomment this when the copy of alert is approved */}
                      {/* <Alert type='info'>
                        <AlertTitle sx={{ paddingLeft: 1, paddingRight: 1, paddingTop: 1 }}>
                          <Typography>Welcome to <strong>Solex Orders</strong>. A platform introducing a new way to conduct Escrow Document Signing using a simple-to-use and intuitive interface.</Typography>
                        </AlertTitle>
                      </Alert> */}
                      <Button
                        sx={{
                          width: '60%',
                          height: 50,
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: 3,
                          marginBottom: 4,
                        }}
                        variant='contained'
                        startIcon={<LoginIcon />}
                        onClick={() => goStartLogin('Okta')}>
                        LOG IN
                      </Button>
                      <Box
                        sx={{
                          borderBottom: 1,
                          borderColor: 'lightgray',
                          width: '60%',
                          display: 'flex',
                          justifyContent: 'center',
                          position: 'relative',
                          marginBottom: 2,
                        }}>
                        <Typography
                          variant='body1'
                          sx={{
                            position: 'absolute',
                            top: -12,
                            paddingLeft: 1,
                            paddingRight: 1,
                            backgroundColor: 'white',
                            color: 'gray',
                          }}>
                          OR
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '60%',
                          display: 'flex',
                          justifyContent: 'center',
                          marginBottom: 5,
                        }}>
                        <Button
                          onClick={() => goStartLogin('EagleID')}
                          variant='text'
                          color='primary'>
                          First American Login
                        </Button>
                      </Box>
                    </>
                  )}

                  {loginType === 'thirdPartyNew' && !guestLoadingAction && (
                    <Box>
                      <Typography
                        sx={{
                          margin: '10px 0px',
                          justifyContent: 'center',
                          alignItems: 'center',
                          display: 'flex',
                        }}
                        variant='body1'
                        color={primaryBlue}>
                        Welcome, {fullName}
                      </Typography>
                      <Alert type='info' variant='outlined'>
                        <AlertTitle
                          sx={{
                            paddingLeft: 1,
                            paddingRight: 1,
                            paddingTop: 1,
                          }}>
                          {orderId ? (
                            <Typography>
                              It looks like you do not have an account yet.
                              Please create an account or continue as guest to
                              view <strong>Solex Orders</strong>.
                            </Typography>
                          ) : (
                            <Typography>
                              It looks like you do not have an account yet.
                              Please create an account to view{' '}
                              <strong>Solex Orders</strong>.
                            </Typography>
                          )}
                        </AlertTitle>
                        {orderId && (
                          <Button
                            size={'small'}
                            variant='text'
                            color='info'
                            onClick={() =>
                              setDifferenceIsOpen(!differenceIsOpen)
                            }
                            endIcon={
                              !differenceIsOpen ? (
                                <KeyboardArrowDownIcon />
                              ) : (
                                <KeyboardArrowUpIcon />
                              )
                            }>
                            <strong> What’s the difference?</strong>
                          </Button>
                        )}
                        {differenceIsOpen && (
                          <Box sx={{ padding: 1 }}>
                            <Typography fontWeight={'600'}>
                              Create New Account
                            </Typography>
                            <Box sx={{ paddingLeft: 3, paddingRight: 3 }}>
                              <ListItemText sx={{ display: 'list-item' }}>
                                Quick and easy one step login process
                              </ListItemText>
                              <ListItemText
                                sx={{
                                  display: 'list-item',
                                  alignItems: 'flex-start',
                                }}>
                                Leverage powerful Agent dashboard to view all
                                past and current orders
                              </ListItemText>
                              <ListItemText sx={{ display: 'list-item' }}>
                                Save time and review all your open orders
                              </ListItemText>
                            </Box>
                            <Typography fontWeight={'600'}>
                              Continue As Guest
                            </Typography>
                            <Box sx={{ paddingLeft: 3, paddingRight: 3 }}>
                              <ListItemText sx={{ display: 'list-item' }}>
                                Use One Time Password and Knowledge Based
                                Authentication to login
                              </ListItemText>
                              <ListItemText sx={{ display: 'list-item' }}>
                                No Agent dashboard
                              </ListItemText>
                              <ListItemText sx={{ display: 'list-item' }}>
                                Can only review a specific order
                              </ListItemText>
                            </Box>
                          </Box>
                        )}
                      </Alert>
                      <Button
                        sx={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: 3,
                          marginBottom: 4,
                        }}
                        variant='contained'
                        startIcon={<PersonAddIcon />}
                        onClick={() => goStartCreateOktaAccount()}>
                        CREATE NEW ACCOUNT
                      </Button>
                      {orderId && (
                        <>
                          <Box
                            sx={{
                              borderBottom: 1,
                              borderColor: 'lightgray',
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              position: 'relative',
                              marginBottom: 2,
                            }}>
                            <Typography
                              variant='body1'
                              sx={{
                                position: 'absolute',
                                top: -12,
                                paddingLeft: 1,
                                paddingRight: 1,
                                backgroundColor: 'white',
                                color: 'gray',
                              }}>
                              OR
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'center',
                              marginBottom: 5,
                            }}>
                            <Button
                              onClick={() => beginGuestAuth(false)}
                              variant='text'
                              color='primary'>
                              CONTINUE AS GUEST
                            </Button>
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                  {guestLoadingAction && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        padding: '10px',
                      }}>
                      <Box p={2}>
                        <Typography variant='h6'>Please Hold</Typography>
                      </Box>
                      <Box p={2}>
                        <CircularProgress />
                      </Box>
                    </Box>
                  )}
                  {loginType === 'guest' &&
                    !guestLoadingAction &&
                    authChallenge &&
                    'CHANNEL_0' in authChallenge && (
                      <>
                        <MFARequestForm
                          questions={authChallenge}
                          cancel={goCancelFlow}
                          goContinueGuestAuth={goContinueGuestAuth}
                          setChannel={setChannel}
                        />
                      </>
                    )}
                  {loginType === 'KBA' &&
                    !guestLoadingAction &&
                    authChallenge &&
                    'KBA_0' in authChallenge && (
                      <KBAForm
                        questions={authChallenge}
                        cancel={goCancelFlow}
                        goNext={goVerifyKBAAnswers}
                      />
                    )}
                  {loginType === 'guest-confirm' && !guestLoadingAction && (
                    <MFAVerifyForm
                      cancel={() => {
                        goCancelFlow();
                        setGuestConfirmationCode('');
                        setChannel(0);
                      }}
                      setGuestConfirmationCode={setGuestConfirmationCode}
                      guestConfirmationCode={guestConfirmationCode}
                      goNext={goVerifyGuestLoginCode}
                      goBack={goBackToMFAOptions}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </div>
    );
  }
}
